import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { type Observable, catchError, map, of, throwError } from 'rxjs';

import { ConfigService, MonitoringLogger } from '@fcom/core';
import { type Location, LocationType } from '@fcom/core-api';
import { finShare } from '@fcom/rx';

import type {
  AmAvailability,
  AmDestination,
  AmDestinationsResponse,
  AmHolidayTypeItem,
  AmLocation,
  AmOrigin,
} from '../interfaces/am-api.interface';
import type { AmRoomSelection } from '../interfaces/paxAmount.interface';
import { formatAmPaxGroup } from '../utils/utils.am';

@Injectable()
export class BookingWidgetAmService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private monitoringLogger = inject(MonitoringLogger);

  getOrigins(): Observable<Location[]> {
    return this.amApiQuery<AmOrigin[]>('origins').pipe(
      map((data: AmOrigin[]) => {
        if (!data) {
          throwError(() => 'Empty origins response from AM');
        }
        return data.map(this.mapAmOriginToLocation);
      }),
      catchError((error: unknown) => {
        this.monitoringLogger.warn('AM origins request failed', { error });
        return of([]);
      }),
      finShare()
    );
  }

  getDestinations(origin: string): Observable<AmLocation[]> {
    return this.amApiQuery<AmDestinationsResponse>(`destinations/${origin}`).pipe(
      map((data: AmDestinationsResponse) => {
        if (!data?.destinations) {
          throwError(() => 'Empty destinations response from AM');
        }
        return data.destinations.map((destination) =>
          this.mapAmDestinationToAmLocation(destination, data.holidayTypes)
        );
      }),
      catchError((error: unknown) => {
        this.monitoringLogger.warn('AM destinations request failed', { error });
        return of([]);
      }),
      finShare()
    );
  }

  getAvailability(
    originLocationCode: string,
    destinationLocationCode: string,
    rooms: AmRoomSelection[]
  ): Observable<AmAvailability> {
    return this.amApiQuery<AmAvailability>('availability', {
      dep: originLocationCode,
      arr: destinationLocationCode,
      g: formatAmPaxGroup(rooms),
    }).pipe(
      catchError((error: unknown) => {
        this.monitoringLogger.warn('AM availability request failed', { error });
        return of({});
      }),
      finShare()
    );
  }

  private mapAmOriginToLocation(amOrigin: AmOrigin): Location {
    return {
      title: amOrigin.city,
      country: '',
      countryCode: amOrigin.countryCode,
      cityName: amOrigin.city,
      locationCode: amOrigin.code,
      AJBPartner: false,
      SJBPartner: false,
      airAward: false,
      airPartner: false,
      airAlliance: false,
      type: LocationType.CITY,
      latitude: 0,
      longitude: 0,
      continentCode: 'eu',
    };
  }

  private mapAmDestinationToAmLocation(
    amDestination: AmDestination,
    holidayTypes: Record<string, AmHolidayTypeItem>
  ): AmLocation {
    return {
      location: {
        title: amDestination.city,
        country: amDestination.country,
        countryCode: amDestination.countryCode,
        cityName: amDestination.city,
        locationCode: amDestination.destinationCode,
        AJBPartner: false,
        SJBPartner: false,
        airAward: false,
        airPartner: false,
        airAlliance: false,
        type: LocationType.CITY,
        latitude: 0,
        longitude: 0,
        continentCode: '',
      },
      holidayType: amDestination.holidayType,
      holidayTypeItem: holidayTypes[amDestination.holidayType],
      searchable: amDestination.searchable,
    };
  }

  private amApiQuery<T>(endpoint: string, params = {}): Observable<T> {
    const apiUrl = this.configService.cfg.amApiUrl;
    return this.http.get<T>(`${apiUrl}/${endpoint}`, { params });
  }
}
